import { useCallback, useState } from "react";

import { useNotification } from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useSitesRepository } from "@/pages/tina/sites/hooks/use-sites-repository";
import type { UnblockSessionRead } from "@/types/tina";

export function useUnblockSession(siteName: string) {
  const repo = useSitesRepository();
  const { open } = useNotification();
  const [session, setSession] = useState<UnblockSessionRead | null>(null);
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState(false);

  const start = useCallback(async () => {
    setStarting(true);
    setSession(null);
    try {
      setSession(await repo.startUnblockSession(siteName));
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to start unblock session",
        description: toHumanErrorMessage(error),
      });
    } finally {
      setStarting(false);
    }
  }, [repo, siteName, open]);

  const save = useCallback(async () => {
    if (!session?.context_id) return;
    setSaving(true);
    try {
      await repo.saveUnblockSession(
        siteName,
        session.session_id,
        session.context_id,
      );
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to save login",
        description: toHumanErrorMessage(error),
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [repo, siteName, session, open]);

  const close = useCallback(async () => {
    if (!session) return;
    try {
      await repo.closeUnblockSession(siteName, session.session_id);
    } catch {
      /* best-effort */
    }
    setSession(null);
  }, [repo, siteName, session]);

  return { start, save, close, starting, saving, session };
}
